import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import WorkflowEditModal from '../components/WorkflowEditModal';
import { getTriageWorkflow, Workflow, updateWorkflow, createWorkflow, getAllWorkflows, deleteWorkflow, WORKFLOW_ITEM_TYPE_OPTIONS } from '../services/workflowService';
import { GEMINI_MODELS } from '../config/constants';
import DuplicateWorkflowModal from 'components/modals/DuplicateWorkflowModal';

const WorkflowManagement: React.FC = () => {
  const history = useHistory();
  const [triageWorkflow, setTriageWorkflow] = useState<Workflow | null>(null);
  const [analysisWorkflows, setAnalysisWorkflows] = useState<Workflow[]>([]);
  const [supportedTypes, setSupportedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [editModal, setEditModal] = useState<{
      isOpen: boolean;
      field: 'description' | 'prompt' | null;
  }>({ isOpen: false, field: null });
  const [newWorkflow, setNewWorkflow] = useState<{ name: string; workflowType: string }>({
      name: '',
      workflowType: WORKFLOW_ITEM_TYPE_OPTIONS[0],
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workflowToDuplicate, setWorkflowToDuplicate] = useState<Workflow | null>(null);

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        setLoading(true);
        const [triage, allWorkflows] = await Promise.all([
          getTriageWorkflow(),
          getAllWorkflows()
        ]);
        setTriageWorkflow(triage);
        setSelectedModel(triage.workflow_json.model);
        console.log('all workflows: ', allWorkflows);

        // Filter out triage workflows to show all other workflows
        const nonTriageWorkflows = allWorkflows.filter(w => w.workflow_type !== 'triage');
        setAnalysisWorkflows(nonTriageWorkflows);
        
        // Extract unique workflow types (excluding triage)
        const uniqueTypes = Array.from(new Set(
          nonTriageWorkflows.map(w => w.workflow_type)
        )).sort();
        setSupportedTypes(uniqueTypes);
        console.log('supported types: ', WORKFLOW_ITEM_TYPE_OPTIONS);
      } catch (err) {
        console.error('Error fetching workflows:', err);
        const error = err;
        setError((error && error.response && error.response.data && error.response.data.message) || 'Failed to load workflows');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflows();
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      const timer = setTimeout(() => setUpdateSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [updateSuccess]);

  const handleModelChange = async (newModel: string) => {
    if (!triageWorkflow) return;
    
    try {
      setIsUpdating(true);
      setUpdateSuccess(false);
      const updatedWorkflow = await updateWorkflow(triageWorkflow.id, {
        workflow_json: {
          ...triageWorkflow.workflow_json,
          model: newModel
        }
      });
      setTriageWorkflow(updatedWorkflow);
      setSelectedModel(newModel);
      setUpdateSuccess(true);
    } catch (err) {
      console.error('Error updating model:', err);
      const error = err;
      alert('Failed to update model: ' + ((error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveField = async (value: string) => {
    if (!triageWorkflow || !editModal.field) return;

    try {
      setIsUpdating(true);
      setUpdateSuccess(false);
      
      let updatedData: any = {};
      
      if (editModal.field === 'description') {
        updatedData.description = value;
      } else if (editModal.field === 'prompt') {
        updatedData.workflow_json = {
          ...triageWorkflow.workflow_json,
          prompt: value
        };
      }
      
      const updatedWorkflow = await updateWorkflow(triageWorkflow.id, updatedData);
      setTriageWorkflow(updatedWorkflow);
      setUpdateSuccess(true);
    } catch (err) {
      console.error('Error updating workflow:', err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAccept = (name: string) => {
    setIsModalOpen(false);
    handleDuplicateWorkflow(name);
  };

  const handleDuplicateWorkflow = async (name: string) => {
    if (!workflowToDuplicate) return;

    try {
      setIsCreating(true);
      const workflow = await createWorkflow({
        name: name.trim(),
        description: 'Enter your description here',
        workflow_type: workflowToDuplicate.workflow_type,
        workflow_json: {
          nodes: workflowToDuplicate.workflow_json.nodes,
          edges: workflowToDuplicate.workflow_json.edges
        },
        is_active: true
      });

      setWorkflowToDuplicate(null);
      setAnalysisWorkflows([...analysisWorkflows, workflow]);
      setNewWorkflow({ name: '', workflowType: WORKFLOW_ITEM_TYPE_OPTIONS[0] });
      setUpdateSuccess(true);
    } catch (err) {
      console.error('Error creating workflow:', err);
      const error = err;
      alert('Failed to create workflow: ' + ((error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Unknown error'));
    } finally {
      setIsCreating(false);
    }
  }

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.name.trim()) {
      alert('Please enter a workflow name');
      return;
    }

    try {
      setIsCreating(true);
      
      // Create default "Triage outcome" node
      const defaultInputNode = {
        id: 'input-node',
        type: 'inputNode',
        position: { x: 50, y: 100 },
        data: { 
          label: 'Input',
          isRemovable: false,
          inputs: [],
          outputs: [
            { id: 'images', label: 'Images', type: 'image[]' }
          ]
        },
        deletable: false,
        sourcePosition: 'right',
        targetPosition: 'left'
      };
      
      const workflow = await createWorkflow({
        name: newWorkflow.name.trim(),
        description: 'Enter your description here',
        workflow_type: newWorkflow.workflowType,
        workflow_json: {
          nodes: [defaultInputNode],
          edges: []
        },
        is_active: true
      });
      
      setAnalysisWorkflows([...analysisWorkflows, workflow]);
      setNewWorkflow({ name: '', workflowType: WORKFLOW_ITEM_TYPE_OPTIONS[0] });
      setUpdateSuccess(true);
    } catch (err) {
      console.error('Error creating workflow:', err);
      const error = err;
      alert('Failed to create workflow: ' + ((error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Unknown error'));
    } finally {
      setIsCreating(false);
    }
  };

  const openEditModal = (field: 'description' | 'prompt') => {
    setEditModal({ isOpen: true, field });
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, field: null });
  };

  const handleDeleteWorkflow = async (workflowId: number, workflowName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click event
    
    const confirmed = window.confirm(
        `Delete "${workflowName}"?\n\nThis action is permanent and can't be undone. Are you sure?`
    );
    
    if (!confirmed) return;
    
    try {
      await deleteWorkflow(workflowId);
      setAnalysisWorkflows(analysisWorkflows.filter(w => w.id !== workflowId));
      
      // Update supported types
      const remainingWorkflows = analysisWorkflows.filter(w => w.id !== workflowId);
      const uniqueTypes = Array.from(new Set(
        remainingWorkflows.map(w => w.workflow_type)
      )).sort();
      setSupportedTypes(uniqueTypes);
      
      setUpdateSuccess(true);
    } catch (err) {
      console.error('Error deleting workflow:', err);
      const error = err;
      alert('Failed to delete workflow: ' + ((error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Unknown error'));
    }
  };

  const truncatePrompt = (prompt: string, maxLength: number = 150): string => {
    if (prompt.length <= maxLength) return prompt;
    return prompt.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Workflow Management
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Create and manage AI processing workflows
          </p>
        </div>

        <div className="card">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Custom Workflows
            </h2>
          </div>
          
          {analysisWorkflows.length === 0 ? (
            <div className="text-center py-12">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No Custom Workflows Yet
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Create a new workflow below to get started
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {analysisWorkflows.map((workflow) => (
                <div
                  key={workflow.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => history.push(`/workflow-editor?id=${workflow.id}`)}
                    >
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {workflow.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {workflow.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                          {workflow.workflow_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => {
                          setIsModalOpen(true);
                          setWorkflowToDuplicate(workflow);
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                        title="Duplicate workflow"
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={(e) => handleDeleteWorkflow(workflow.id, workflow.name, e)}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                        title="Delete workflow"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => history.push(`/workflow-editor?id=${workflow.id}`)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Create New Workflow
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={newWorkflow.name}
                  onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
                  placeholder="e.g., Stamp Analysis"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={newWorkflow.workflowType}
                  onChange={(e) => setNewWorkflow({ ...newWorkflow, workflowType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {WORKFLOW_ITEM_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={handleCreateWorkflow}
              disabled={isCreating}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Workflow'}
            </button>
        </div>
      </div>

      {triageWorkflow && editModal.field && (
        <WorkflowEditModal
          isOpen={editModal.isOpen}
          onClose={closeEditModal}
          onSave={handleSaveField}
          title={editModal.field === 'description' ? 'Edit Description' : 'Edit Prompt'}
          initialValue={
            editModal.field === 'description'
              ? triageWorkflow.description
              : triageWorkflow.workflow_json.prompt
          }
          fieldName={editModal.field === 'description' ? 'Description' : 'Prompt'}
          isTextArea={editModal.field === 'prompt'}
        />
      )}
      <DuplicateWorkflowModal 
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false); 
          setWorkflowToDuplicate(null);
        }}
        onAccept={(name: string) => handleAccept(name)}
      />
    </div>
  );
};

export default WorkflowManagement;
